import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { EASE, DURATION } from './motion';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastOptions {
  /**
   * A single inline action — "Undo", almost always. Forgiveness beats
   * confirmation: a reversible action can act immediately and offer the way
   * back, instead of interrupting with "are you sure?" beforehand.
   */
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastApi {
  /** Confirms a completed action. Auto-dismisses. */
  success: (message: string, options?: ToastOptions) => void;
  /** Explains a failure. Sticks around longer so it can actually be read. */
  error: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** How long each variant stays. Errors linger: they carry the words that
 *  tell the user what to do next, and 4 seconds is not enough to read them. */
const DURATION_MS: Record<ToastVariant, number> = {
  success: 4000,
  info: 5000,
  error: 8000,
};

/** A toast carrying an action stays long enough for the decision to happen. */
const ACTION_DURATION_MS = 8000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string, options?: ToastOptions) => {
      const id = nextId.current++;
      setToasts((current) => [
        ...current.slice(-3),
        { id, variant, message, actionLabel: options?.actionLabel, onAction: options?.onAction },
      ]);
      const duration = options?.actionLabel ? ACTION_DURATION_MS : DURATION_MS[variant];
      window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message, options) => push('success', message, options),
      error: (message, options) => push('error', message, options),
      info: (message, options) => push('info', message, options),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="ui-toastStack" aria-live="polite" aria-atomic="false">
          <AnimatePresence>
            {toasts.map((toast) => (
              <motion.div
                key={toast.id}
                layout
                initial={{ opacity: 0, y: 16, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                transition={{ duration: DURATION.base, ease: EASE }}
                className={`ui-toast ui-toast--${toast.variant}`}
                role={toast.variant === 'error' ? 'alert' : 'status'}
              >
                <span className="ui-toastIcon" aria-hidden="true">
                  {toast.variant === 'success' ? '✓' : toast.variant === 'error' ? '!' : 'i'}
                </span>
                <span className="ui-toastMessage">{toast.message}</span>
                {toast.actionLabel && toast.onAction && (
                  <button
                    type="button"
                    className="ui-toastAction"
                    onClick={() => {
                      toast.onAction?.();
                      dismiss(toast.id);
                    }}
                  >
                    {toast.actionLabel}
                  </button>
                )}
                <button
                  type="button"
                  className="ui-toastDismiss"
                  aria-label="Dismiss"
                  onClick={() => dismiss(toast.id)}
                >
                  ×
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/**
 * Feedback for actions. Every mutation should either confirm (success) or
 * explain (error) — a silent catch block is a user staring at a button
 * wondering whether anything happened.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
}
